// Small building blocks the auth screens share (spec/07 §3.1, §6.5): a failure banner, and a labelled
// field driven by react-hook-form. Factored out of `SignInPage.tsx` so `SignUpPage.tsx` and
// `InvitationAcceptPage.tsx` render the same controls rather than their own copies.

import { TextField } from '@clavian/ui-web';
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';

export function Failure({ message }: { readonly message: string | null }) {
  return message === null ? null : (
    <p className="console-alert" role="alert">
      {message}
    </p>
  );
}

export interface TextInputProps<TValues extends FieldValues> {
  readonly control: Control<TValues>;
  readonly name: Path<TValues>;
  readonly label: string;
  readonly description?: string;
  readonly type?: string;
  readonly autoComplete?: string;
  readonly inputMode?: 'numeric' | 'tel' | 'email' | 'text';
  readonly numeric?: boolean;
  readonly rules: { required: string; validate?: (value: string) => true | string };
}

/** A kit `TextField` driven by react-hook-form, with the rule's message as its error. */
export function TextInput<TValues extends FieldValues>({ control, name, rules, ...input }: TextInputProps<TValues>) {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules as never}
      render={({ field, fieldState }) => (
        <TextField
          {...input}
          required
          name={field.name}
          value={field.value as string}
          onChange={field.onChange}
          onBlur={field.onBlur}
          error={fieldState.error?.message}
        />
      )}
    />
  );
}
